import { useCallback, useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { Button } from "@humansignal/ui";
import { syncStateRevAtom } from "./syncModalAtom";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./SyncModal.prefix.css";

const block = cn("sync-modal");

const getCsrf = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const fetchJson = async (url, init = {}) => {
  const r = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrf(),
      ...(init.headers || {}),
    },
  });
  const text = await r.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    const looksLikeHtml = /^\s*<!doctype|^\s*<html/i.test(text);
    body = looksLikeHtml
      ? { error: `${r.status} ${r.statusText} (server returned an HTML error page)` }
      : { error: text.slice(0, 240) };
  }
  return { ok: r.ok, status: r.status, body };
};

const opLabel = (op) =>
  ({
    create: "New",
    update: "Edit",
    delete: "Delete",
    create_from_cloud: "Pull",
  })[op] || "Edit";

const opTone = (op) =>
  ({
    create: "create",
    update: "update",
    delete: "delete",
    create_from_cloud: "pull",
  })[op] || "update";

/** Compact single-line row used by the auto-resolved sections
 *  (will-push / will-pull). Shows op + project + summary in one row. */
const QuietRow = ({ row }) => (
  <li className={block.elem("qrow").toClassName()}>
    <span
      className={block
        .elem("op")
        .mod({ tone: opTone(row.op) })
        .toClassName()}
    >
      {opLabel(row.op)}
    </span>
    <span className={block.elem("qrow-title").toClassName()} title={row.project_title}>
      {row.project_title || "Project"}
      {row.task_id ? <span className={block.elem("qrow-task").toClassName()}>{`, #${row.task_id}`}</span> : null}
    </span>
    <span className={block.elem("qrow-summary").toClassName()} title={row.local_summary || row.cloud_summary}>
      {row.op === "create_from_cloud" ? row.cloud_summary : row.local_summary}
    </span>
  </li>
);

/** Conflict row — keeps the side-by-side diff because the user has
 *  to compare local vs cloud to pick a side. */
const ConflictRow = ({ row, choice, onChoose }) => {
  const { t } = useTranslation();
  return (
    <li className={block.elem("crow").toClassName()}>
      <div className={block.elem("crow-head").toClassName()}>
        <span className={block.elem("op").mod({ tone: "update" }).toClassName()}>
          {t("pages.userprofile.sync_modal.conflict")}
        </span>
        <span className={block.elem("crow-title").toClassName()} title={row.project_title}>
          {row.project_title || "Project"}
          {row.task_id ? <span className={block.elem("qrow-task").toClassName()}>{`, #${row.task_id}`}</span> : null}
        </span>
      </div>
      <div className={block.elem("diff").toClassName()}>
        <div className={block.elem("side").mod({ side: "local" }).toClassName()}>
          <div className={block.elem("side-label").toClassName()}>{t("pages.userprofile.sync_modal.local")}</div>
          <div className={block.elem("side-body").toClassName()}>{row.local_summary}</div>
        </div>
        <div className={block.elem("side").mod({ side: "cloud" }).toClassName()}>
          <div className={block.elem("side-label").toClassName()}>{t("pages.userprofile.sync_modal.cloud")}</div>
          <div className={block.elem("side-body").toClassName()}>{row.cloud_summary}</div>
        </div>
      </div>
      <div className={block.elem("choices").toClassName()} role="radiogroup">
        {[
          ["ours", "Local"],
          ["theirs", "Cloud"],
          ["both", "Both"],
          ["skip", "Skip"],
        ].map(([val, label]) => (
          <label
            key={val}
            className={block
              .elem("choice")
              .mod({ active: choice === val })
              .toClassName()}
          >
            <input
              type="radio"
              name={`r-${row.meta_id ?? row.cloud_id}`}
              checked={choice === val}
              onChange={() => onChoose(val)}
            />
            {label}
          </label>
        ))}
      </div>
    </li>
  );
};

/**
 * Sync modal — explicit handshake before push/pull.
 *
 * Flow: open → POST /preview/ → user resolves any conflicts → POST
 * /apply/. Auto-resolved rows (only one side changed) flow through
 * silently as compact line items. Only true conflicts (both sides
 * changed since last sync) get the side-by-side picker.
 */
export const SyncModal = ({ open, onClose, onComplete }) => {
  const { t } = useTranslation();
  // Phases: "overview" (per-project list — the entry screen) →
  // "preview" (row-by-row review, scoped to one project) → "done".
  const [phase, setPhase] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [result, setResult] = useState(null);
  // Per-project rows from /api/afri/sync/overview/ (local knowledge,
  // instant, works offline).
  const [overview, setOverview] = useState(null);
  // cloud_project_id → "push" | "pull" while that row's action runs.
  const [rowBusy, setRowBusy] = useState({});
  // Which project the preview/apply flow is scoped to (null = all).
  const [scopedCloudId, setScopedCloudId] = useState(null);
  // Live progress of a sync that's already running in the background (the
  // post-login/refresh pull), so opening this modal mid-sync shows "N rows
  // pulled" instead of a blank body while the preview call is in flight.
  const [progress, setProgress] = useState(null);
  const bumpSyncRev = useSetAtom(syncStateRevAtom);

  const loadOverview = useCallback(async () => {
    try {
      const r = await fetch("/api/afri/sync/overview/?probe_cloud=1", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (r.ok) setOverview((await r.json()).projects || []);
    } catch {
      /* offline / transient — the list just doesn't refresh */
    }
  }, []);

  const runPreview = useCallback(async (cloudProjectId = null) => {
    setBusy(true);
    setError(null);
    const r = await fetchJson("/api/afri/sync/preview/", {
      method: "POST",
      body: JSON.stringify(cloudProjectId ? { cloud_project_id: cloudProjectId } : {}),
    });
    setBusy(false);
    if (!r.ok) {
      // Translate code-stamped backend errors into the short
      // operator-readable strings the user expects to see in this
      // modal — the raw ``HTTPConnectionPool(...)`` stack from
      // requests is unreadable noise for an offline scenario.
      const code = r.body?.code;
      const msg =
        code === "cloud_unreachable"
          ? "Cloud is unreachable right now. Your changes are saved locally and will sync when it's back."
          : code === "cloud_auth_failed"
            ? "Cloud rejected your token. Sign in again from the Cloud sync tab."
            : code === "no_cloud_url"
              ? "No cloud URL configured. Open Settings → Cloud sync."
              : r.body?.error || `Preview failed (${r.status})`;
      setError(msg);
      return;
    }
    setPreview(r.body);
    const init = {};
    for (const row of r.body.local_only || []) {
      if (row.meta_id != null) init[`meta:${row.meta_id}`] = "ours";
    }
    for (const row of r.body.cloud_only || []) {
      if (row.meta_id != null) init[`meta:${row.meta_id}`] = "theirs";
      else if (row.cloud_id != null) init[`cloud:${row.cloud_id}`] = "theirs";
    }
    for (const row of r.body.conflicts || []) {
      if (row.meta_id != null) init[`meta:${row.meta_id}`] = null;
    }
    setResolutions(init);
  }, []);

  useEffect(() => {
    if (!open) return;
    setPhase("overview");
    setError(null);
    setPreview(null);
    setResolutions({});
    setResult(null);
    setOverview(null);
    setRowBusy({});
    setScopedCloudId(null);
    loadOverview();
  }, [open, loadOverview]);

  // Deliberate per-project actions. Push/pull refresh the overview
  // when they land so the counts the user acted on visibly clear.
  const rowAction = useCallback(
    async (cloudId, kind) => {
      setRowBusy((prev) => ({ ...prev, [cloudId]: kind }));
      setError(null);
      try {
        const r = await fetchJson(`/api/afri/sync/${kind}/`, {
          method: "POST",
          body: JSON.stringify({ cloud_project_id: cloudId }),
        });
        if (!r.ok) {
          const code = r.body?.code;
          setError(
            code === "cloud_unreachable"
              ? t("pages.userprofile.sync_modal.cloud_unreachable_retry")
              : r.body?.error || `${kind} failed (${r.status})`,
          );
        }
      } finally {
        setRowBusy((prev) => {
          const next = { ...prev };
          delete next[cloudId];
          return next;
        });
        bumpSyncRev((rev) => rev + 1);
        loadOverview();
      }
    },
    [loadOverview, bumpSyncRev, t],
  );

  // Manual-sync toggle: excludes the project from autopush/autopull;
  // its queue only moves via these per-project buttons.
  const setManual = useCallback(
    async (cloudId, manual) => {
      try {
        await fetchJson("/api/afri/sync/set_sync_mode/", {
          method: "POST",
          body: JSON.stringify({ cloud_project_id: cloudId, manual }),
        });
      } finally {
        loadOverview();
      }
    },
    [loadOverview],
  );

  const reviewProject = useCallback(
    (cloudId) => {
      setScopedCloudId(cloudId);
      setPreview(null);
      setResolutions({});
      setPhase("preview");
      runPreview(cloudId);
    },
    [runPreview],
  );

  const backToOverview = useCallback(() => {
    setScopedCloudId(null);
    setPreview(null);
    setResolutions({});
    setError(null);
    setPhase("overview");
    loadOverview();
  }, [loadOverview]);

  // Poll background-sync progress while the modal is open, so a pull that's
  // already running shows a live row count.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch("/api/afri/sync/progress/", { credentials: "same-origin", cache: "no-store" });
        const b = await r.json();
        if (alive) setProgress(b);
      } catch {
        /* ignore — best effort */
      }
    };
    tick();
    const id = window.setInterval(tick, 1500);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [open]);

  const setChoice = (key, val) => setResolutions((prev) => ({ ...prev, [key]: val }));

  const conflictsResolved = (preview?.conflicts || []).every(
    (row) => row.meta_id != null && resolutions[`meta:${row.meta_id}`] != null,
  );
  const pushCount = preview?.local_only?.length || 0;
  const pullCount = preview?.cloud_only?.length || 0;
  const conflictCount = preview?.conflicts?.length || 0;
  const total = pushCount + pullCount + conflictCount;

  const apply = async () => {
    if (!conflictsResolved) {
      setError("Pick a resolution for every conflict before applying.");
      return;
    }
    setBusy(true);
    setError(null);
    const cleaned = {};
    for (const [k, v] of Object.entries(resolutions)) {
      if (v) cleaned[k] = v;
    }
    const r = await fetchJson("/api/afri/sync/apply/", {
      method: "POST",
      body: JSON.stringify(
        scopedCloudId ? { resolutions: cleaned, cloud_project_id: scopedCloudId } : { resolutions: cleaned },
      ),
    });
    setBusy(false);
    if (!r.ok) {
      const code = r.body?.code;
      const msg =
        code === "cloud_unreachable"
          ? "Cloud is unreachable right now. Your changes are still saved locally, try again when it's back."
          : code === "cloud_auth_failed"
            ? "Cloud rejected your token. Sign in again from the Cloud sync tab."
            : r.body?.error || `Apply failed (${r.status})`;
      setError(msg);
      return;
    }
    setResult(r.body);
    setPhase("done");
    // Bump the global rev so the menubar pill + any other pending-
    // count surface re-polls immediately instead of waiting for its
    // next periodic tick.
    bumpSyncRev((rev) => rev + 1);
    onComplete?.();
  };

  if (!open) return null;

  // Button label is explicit so it never collapses to an empty
  // string just because counts happen to be zero.
  const applyLabel = busy
    ? "Working…"
    : conflictCount > 0 && !conflictsResolved
      ? "Resolve conflicts to continue"
      : total === 0
        ? "Nothing to sync"
        : `Sync ${total} change${total === 1 ? "" : "s"}`;

  return (
    <div
      className={block.elem("overlay").toClassName()}
      onClick={(e) => {
        // Always dismissable — clicking the backdrop closes even mid-sync.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={block.toClassName()}
        role="dialog"
        aria-modal="true"
        aria-label={t("pages.userprofile.sync_modal.sync_changes")}
      >
        <div className={block.elem("header").toClassName()}>
          <h3 className={block.elem("title").toClassName()}>
            {phase === "done"
              ? "Sync complete"
              : phase === "overview"
                ? t("pages.userprofile.sync_modal.sync_by_project")
                : "Sync changes"}
          </h3>
          <button type="button" className={block.elem("close").toClassName()} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={block.elem("body").toClassName()}>
          {error && (
            <div role="alert" className={block.elem("err").toClassName()}>
              {error}
            </div>
          )}

          {/* ── Overview: the per-project list. Nothing syncs from here
              without a deliberate click on that project's own button. ── */}
          {phase === "overview" && !overview && (
            <div className={block.elem("empty").toClassName()}>
              <span className={block.elem("spinner").toClassName()} aria-hidden />
            </div>
          )}

          {phase === "overview" && overview && overview.length === 0 && (
            <div className={block.elem("empty").toClassName()}>
              <div className={block.elem("empty-glyph").toClassName()}>✓</div>
              <div className={block.elem("empty-title").toClassName()}>
                {t("pages.userprofile.sync_modal.all_projects_synced")}
              </div>
            </div>
          )}

          {phase === "overview" && overview && overview.length > 0 && (
            <ul className={block.elem("plist").toClassName()}>
              {overview.map((p) => (
                <li key={p.cloud_id} className={block.elem("prow").toClassName()}>
                  <div className={block.elem("prow-main").toClassName()}>
                    <div className={block.elem("prow-title").toClassName()}>{p.title}</div>
                    <div className={block.elem("prow-stats").toClassName()}>
                      {p.to_push > 0 && (
                        <span className={block.elem("stat").mod({ tone: "create" }).toClassName()}>
                          ↑ <strong>{p.to_push}</strong> {t("pages.userprofile.sync_modal.to_push")}
                        </span>
                      )}
                      {p.conflicts > 0 && (
                        <span className={block.elem("stat").mod({ tone: "conflict" }).toClassName()}>
                          ⚠ <strong>{p.conflicts}</strong> {t("pages.userprofile.sync_modal.conflicts_label")}
                        </span>
                      )}
                      {p.cloud_changes === true && (
                        <span className={block.elem("stat").mod({ tone: "pull" }).toClassName()}>
                          ↓ {t("pages.userprofile.sync_modal.changes_on_cloud")}
                        </span>
                      )}
                      {p.to_push === 0 && p.conflicts === 0 && p.cloud_changes !== true && (
                        <span className={block.elem("stat").toClassName()}>
                          {t("pages.userprofile.sync_modal.up_to_date")}
                        </span>
                      )}
                      <span className={block.elem("prow-tier").toClassName()}>
                        {p.downloaded
                          ? t("pages.userprofile.sync_modal.downloaded")
                          : t("pages.userprofile.sync_modal.online_only")}
                      </span>
                      <label
                        className={block.elem("prow-manual").toClassName()}
                        title={t("pages.userprofile.sync_modal.manual_sync_hint")}
                      >
                        <input
                          type="checkbox"
                          checked={!!p.manual_sync}
                          onChange={(e) => setManual(p.cloud_id, e.target.checked)}
                        />
                        {t("pages.userprofile.sync_modal.manual_sync_label")}
                      </label>
                    </div>
                  </div>
                  <div className={block.elem("prow-actions").toClassName()}>
                    {p.conflicts > 0 && (
                      <Button
                        size="small"
                        variant="negative"
                        look="outlined"
                        disabled={!!rowBusy[p.cloud_id]}
                        onClick={() => reviewProject(p.cloud_id)}
                      >
                        {t("pages.userprofile.sync_modal.review")}
                      </Button>
                    )}
                    <Button
                      size="small"
                      look="outlined"
                      disabled={p.to_push === 0 || !!rowBusy[p.cloud_id]}
                      onClick={() => rowAction(p.cloud_id, "push")}
                      aria-label={`Push ${p.title}`}
                    >
                      {rowBusy[p.cloud_id] === "push" ? "…" : `↑ ${t("pages.userprofile.sync_modal.push")}`}
                    </Button>
                    {p.downloaded && (
                      <Button
                        size="small"
                        look="outlined"
                        variant="neutral"
                        disabled={!!rowBusy[p.cloud_id]}
                        onClick={() => rowAction(p.cloud_id, "pull")}
                        aria-label={`Pull ${p.title}`}
                      >
                        {rowBusy[p.cloud_id] === "pull" ? "…" : `↓ ${t("pages.userprofile.sync_modal.pull")}`}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* A background sync is actively running — show live progress and
              hide the (racing) preview until it finishes. */}
          {phase === "preview" && progress?.in_progress && (
            <div className={block.elem("empty").toClassName()}>
              <span className={block.elem("spinner").toClassName()} aria-hidden />
              <div className={block.elem("empty-title").toClassName()}>
                {progress.direction === "push" ? "Uploading changes…" : "Downloading from cloud…"}
              </div>
              <div className={block.elem("empty-sub").toClassName()}>
                {(progress.rows_processed || 0).toLocaleString()} row{progress.rows_processed === 1 ? "" : "s"} synced
                so far
                {progress.rows_failed ? `, ${progress.rows_failed} failed` : ""}
              </div>
            </div>
          )}

          {/* Preview still loading (and nothing running yet). */}
          {phase === "preview" && !preview && !error && !progress?.in_progress && (
            <div className={block.elem("empty").toClassName()}>
              <span className={block.elem("spinner").toClassName()} aria-hidden />
              <div className={block.elem("empty-title").toClassName()}>Checking for changes…</div>
            </div>
          )}

          {phase === "preview" && preview && !progress?.in_progress && (
            <>
              {total === 0 && !error && (
                <div className={block.elem("empty").toClassName()}>
                  <div className={block.elem("empty-glyph").toClassName()}>✓</div>
                  <div className={block.elem("empty-title").toClassName()}>
                    {t("pages.userprofile.sync_modal.nothing_to_sync")}
                  </div>
                  <div className={block.elem("empty-sub").toClassName()}>
                    {preview?.totals
                      ? `Compared ${preview.totals.local_annotations} local + ${preview.totals.cloud_annotations} cloud annotation${preview.totals.cloud_annotations === 1 ? "" : "s"}, ${preview.totals.matched} match.`
                      : "Local and cloud already agree on every annotation."}
                  </div>
                </div>
              )}

              {total > 0 && (
                <>
                  <div className={block.elem("summary").toClassName()}>
                    {pushCount > 0 && (
                      <span className={block.elem("stat").mod({ tone: "create" }).toClassName()}>
                        <strong>{pushCount}</strong> to push
                      </span>
                    )}
                    {pullCount > 0 && (
                      <span className={block.elem("stat").mod({ tone: "pull" }).toClassName()}>
                        <strong>{pullCount}</strong> to pull
                      </span>
                    )}
                    {conflictCount > 0 && (
                      <span className={block.elem("stat").mod({ tone: "conflict" }).toClassName()}>
                        <strong>{conflictCount}</strong> conflict{conflictCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>

                  {conflictCount > 0 && (
                    <section className={block.elem("section").toClassName()}>
                      <h4 className={block.elem("section-title").toClassName()}>Conflicts — pick a side</h4>
                      <ul className={block.elem("list").toClassName()}>
                        {preview.conflicts.map((row) => (
                          <ConflictRow
                            key={`c-${row.meta_id ?? row.cloud_id}`}
                            row={row}
                            choice={resolutions[`meta:${row.meta_id}`] || null}
                            onChoose={(v) => setChoice(`meta:${row.meta_id}`, v)}
                          />
                        ))}
                      </ul>
                    </section>
                  )}

                  {pushCount > 0 && (
                    <section className={block.elem("section").toClassName()}>
                      <h4 className={block.elem("section-title").toClassName()}>Will push — local edits</h4>
                      <ul className={block.elem("list").toClassName()}>
                        {preview.local_only.map((row) => (
                          <QuietRow key={`lo-${row.meta_id}`} row={row} />
                        ))}
                      </ul>
                    </section>
                  )}

                  {pullCount > 0 && (
                    <section className={block.elem("section").toClassName()}>
                      <h4 className={block.elem("section-title").toClassName()}>Will pull — new on cloud</h4>
                      <ul className={block.elem("list").toClassName()}>
                        {preview.cloud_only.map((row) => (
                          <QuietRow key={`co-${row.meta_id ?? row.cloud_id}`} row={row} />
                        ))}
                      </ul>
                    </section>
                  )}
                </>
              )}
            </>
          )}

          {phase === "done" && result && (
            <div className={block.elem("done").toClassName()}>
              <div className={block.elem("empty-glyph").toClassName()}>✓</div>
              <div className={block.elem("empty-title").toClassName()}>
                Synced {result.rows_processed} change{result.rows_processed === 1 ? "" : "s"}
              </div>
              {result.rows_failed ? (
                <div className={block.elem("empty-sub").mod({ tone: "err" }).toClassName()}>
                  {result.rows_failed} failed{result.error ? `. ${result.error}` : ""}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className={block.elem("footer").toClassName()}>
          {phase === "overview" && (
            <Button size="small" look="outlined" variant="neutral" onClick={onClose}>
              Close
            </Button>
          )}
          {phase === "preview" && (
            <>
              <Button size="small" look="outlined" variant="neutral" onClick={backToOverview}>
                {t("pages.userprofile.sync_modal.back")}
              </Button>
              <Button
                size="small"
                variant="primary"
                onClick={apply}
                disabled={busy || !preview || total === 0 || !conflictsResolved}
              >
                {applyLabel}
              </Button>
            </>
          )}
          {phase === "done" && (
            <Button size="small" variant="primary" onClick={backToOverview}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
